import ar from './locale/ar';
import en from './locale/en';

// Eltafouk uses Arabic (default) + English (fallback), so only these two
// locales are bundled eagerly. Every other locale is code-split and lazy-loaded
// on demand via `ensureLocaleMessages`. Previously all ~40 locales were imported
// eagerly, which added ~10MB (uncompressed) to the very first page load.
const messages = { ar, en };

// One lazy Vite chunk per remaining supported locale. This list mirrors the
// locales the app previously imported eagerly (minus ar/en, bundled above) and
// is explicit on purpose: globbing `./locale/*` would also pull in incomplete
// locale folders that exist on disk (e.g. zh, am, az) and break the build.
const lazyLocales = import.meta.glob([
  './locale/bg/index.js',
  './locale/ca/index.js',
  './locale/cs/index.js',
  './locale/da/index.js',
  './locale/de/index.js',
  './locale/el/index.js',
  './locale/es/index.js',
  './locale/fa/index.js',
  './locale/fi/index.js',
  './locale/fr/index.js',
  './locale/he/index.js',
  './locale/hi/index.js',
  './locale/hu/index.js',
  './locale/id/index.js',
  './locale/is/index.js',
  './locale/it/index.js',
  './locale/ja/index.js',
  './locale/ko/index.js',
  './locale/lt/index.js',
  './locale/lv/index.js',
  './locale/ml/index.js',
  './locale/nl/index.js',
  './locale/no/index.js',
  './locale/pl/index.js',
  './locale/pt/index.js',
  './locale/pt_BR/index.js',
  './locale/ro/index.js',
  './locale/ru/index.js',
  './locale/sk/index.js',
  './locale/sr/index.js',
  './locale/sv/index.js',
  './locale/ta/index.js',
  './locale/th/index.js',
  './locale/tr/index.js',
  './locale/uk/index.js',
  './locale/vi/index.js',
  './locale/zh_CN/index.js',
  './locale/zh_TW/index.js',
]);

/**
 * Ensure `locale`'s messages are registered on the i18n instance, lazy-loading
 * them if they are not bundled/loaded yet. Returns immediately for an
 * already-available locale (ar/en or one loaded earlier).
 *
 * @param {import('vue-i18n').Composer} i18n - the global i18n composer
 * @param {string} locale
 * @returns {Promise<boolean>} true if `locale` is now available; false for an
 *   unknown locale or a failed lazy import (callers should not switch to it).
 */
export const ensureLocaleMessages = async (i18n, locale) => {
  if (!locale) return false;
  if (i18n.availableLocales.includes(locale)) return true;
  const importLocale = lazyLocales[`./locale/${locale}/index.js`];
  if (!importLocale) return false;
  try {
    const { default: localeMessages } = await importLocale();
    i18n.setLocaleMessage(locale, localeMessages);
    return true;
  } catch {
    return false;
  }
};

export default messages;
